import {
    CONTACT_DISPLAY_FIELD_ORDER,
    CONTACT_FIELD_DISPLAY_LABELS,
    CONTACT_ID_UPLOAD_FIELDS,
} from '../constants/contactFields';
import type { QuestionSchema } from '../types/templateFormFields';
import {
    getContactBlockConfig,
    getContactBlockEntryTitle,
    getFilledContactBlockEntries,
} from './contactBlock';
import {
    formatContactFormFieldDisplayValue,
    formatIntakeFieldDisplayValue,
    formatIntakeTableDisplayValue,
    type IntakeTableDisplayValue,
    parseQuestionSchema,
} from './intakeFieldFormatting';
import { getDelimitedFileIds } from './intakeUploads';
import { getVisibleQuestions } from './questionVisibility';

export type ContactBlockDisplayItem =
    | { kind: 'text'; label: string; value: string }
    | { kind: 'files'; label: string; fileIds: string[] }
    | { kind: 'table'; label: string; table: IntakeTableDisplayValue };

export interface ContactBlockEntryDisplay {
    entryId: string;
    title: string;
    items: ContactBlockDisplayItem[];
}

/**
 * Display-ready entries for a contact block answer, skipping blank entries. Contact fields are
 * included only when answered; visible nested questions are always included so callers can apply
 * their own empty-answer policy.
 */
export function formatContactBlockDisplayValue(
    value: unknown,
    questionSchema?: QuestionSchema | string | null,
): ContactBlockEntryDisplay[] {
    const schema = parseQuestionSchema(questionSchema);
    if (schema?.type !== 'contactInfoSection') return [];

    const config = getContactBlockConfig(schema);
    return getFilledContactBlockEntries(value, schema).map((entry, index, entries) => {
        const contactItems = CONTACT_DISPLAY_FIELD_ORDER.flatMap<ContactBlockDisplayItem>((key) => {
            const displayValue = formatContactFormFieldDisplayValue(key, entry.contact[key]);
            if (!displayValue) return [];
            return [
                { kind: 'text', label: CONTACT_FIELD_DISPLAY_LABELS[key], value: displayValue },
            ];
        });

        const idItems = CONTACT_ID_UPLOAD_FIELDS.flatMap<ContactBlockDisplayItem>(
            ({ field, title }) => {
                const fileIds = getDelimitedFileIds(entry.contact[field]);
                if (fileIds.length === 0) return [];
                return [{ kind: 'files', label: title, fileIds }];
            },
        );

        const questionItems = getVisibleQuestions(
            config.questions ?? [],
            (question) => entry.answers[question.questionSchema.name],
        ).map<ContactBlockDisplayItem>((question) => {
            const answer = entry.answers[question.questionSchema.name];
            switch (question.questionSchema.type) {
                case 'upload':
                    return {
                        kind: 'files',
                        label: question.title,
                        fileIds: getDelimitedFileIds(answer),
                    };
                case 'table':
                    return {
                        kind: 'table',
                        label: question.title,
                        table: formatIntakeTableDisplayValue(answer, question.questionSchema),
                    };
                default:
                    return {
                        kind: 'text',
                        label: question.title,
                        value: formatIntakeFieldDisplayValue(answer, question.questionSchema),
                    };
            }
        });

        return {
            entryId: entry.entryId,
            title: getContactBlockEntryTitle(config.entryLabel, index, entries.length),
            items: [...contactItems, ...idItems, ...questionItems],
        };
    });
}
